import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { DestinationPickerPopover } from 'components/DestinationPickerPopover';
import { OnDestinationChangeParams, OpenedFormControl, RecentSearchParams, useSearchBox, useSearchBoxDestinationPicker } from 'hooks/searchBox';
import { useContentHotelContext } from 'modules/accommodation/ContentHotelProvider';
import { setSearchData } from 'modules/search/slice';
import { getOpenDatePickerType } from 'modules/searchBox/selectors';
import { setOpenDatePickerType } from 'modules/searchBox/slice';
import { SearchTrigger } from 'modules/searchBox/types';
import { toggle } from 'opticks';
import { getDestinationParameter } from 'utils/searchParams';
import { PopoverPlacement } from '@daedalus/atlas/Popover';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import { roomsSplitToString, roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { POPOVER_HORIZONTAL_OFFSET, POPOVER_VERTICAL_OFFSET } from '@daedalus/shared/src/search/constants';
import { DatePickerPopover } from '@daedalus/shared/src/search/DatePickerPopover';
import { GuestPickerPopover } from '@daedalus/shared/src/search/GuestPickerPopover';
import { SearchFormHorizontal } from '@daedalus/shared/src/search/SearchFormHorizontal';
import { SearchFormHorizontalFullWidth } from '@daedalus/shared/src/whiteLabel/SearchFormHorizontalFullWidth';
import { UpdateSearch } from '.';
const FormContainer = /*#__PURE__*/styled('div')({
  name: "FormContainer",
  class: "f14rn1h7",
  propsAsIs: false
});
interface Props {
  checkIn: string;
  checkOut: string;
  rooms: string;
  updateSearch: UpdateSearch;
}
export const SearchForm = ({
  checkIn,
  checkOut,
  rooms,
  updateSearch
}: Props) => {
  const searchBoxWithDestinationPicker = toggle('8d14a737-rsp-v2-improvements', false, true);
  const {
    hotel
  } = useContentHotelContext();
  const {
    doSearch
  } = useSearchBox();
  const i18n = useContext(ConfigureI18nContext);
  const dispatch = useDispatch();
  const dispatchTeamEvent = useDispatchTeamEvent();
  const containerRef = useRef<HTMLDivElement>(null);
  const guestPickerRef = useRef<HTMLDivElement>(null);
  const stateOpenedDatePickerType = useSelector(getOpenDatePickerType);
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(OpenedFormControl.NONE);
  const {
    brandHasAlternativeSearchForm
  } = useBrand();
  const destinationInputRef = useRef<HTMLDivElement>(null);

  // Input state
  const [newCheckIn, setNewCheckIn] = useState<string>(checkIn);
  const [newCheckOut, setNewCheckOut] = useState<string>(checkOut);
  const [newRooms, setNewRooms] = useState<string>(rooms);
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    roomsSplit
  } = roomsToConfigurationObject(newRooms);
  useEffect(() => {
    if (stateOpenedDatePickerType === DatePickerType.CheckIn) {
      setOpenedFormControl(OpenedFormControl.CHECK_IN);
    }
  }, [stateOpenedDatePickerType]);
  useEffect(() => {
    if (hotel) {
      dispatch(setSearchData({
        objectDisplayName: hotel.hotelName
      }));
    }
  }, [dispatch, hotel]);

  // Keep internal/external state in sync
  useEffect(() => {
    setNewCheckIn(checkIn);
    setNewCheckOut(checkOut);
    setNewRooms(rooms);
  }, [checkIn, checkOut, rooms]);
  const trackDatePickerClick = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.DatePickerInput,
      action: Action.Clicked
    }));
  };
  const handleReset = () => {
    setNewCheckIn(checkIn);
    setNewCheckOut(checkOut);
    setNewRooms(rooms);
  };
  const handleSubmit = () => {
    updateSearch({
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      rooms: newRooms,
      searchTrigger: SearchTrigger.Submit
    });
  };
  const handleSearchButtonClick = () => {
    handleSubmit();
  };
  const handleCheckInClick = () => {
    trackDatePickerClick();
    setOpenedFormControl(OpenedFormControl.CHECK_IN);
  };
  const handleCheckOutClick = () => {
    trackDatePickerClick();
    setOpenedFormControl(OpenedFormControl.CHECK_OUT);
  };
  const handleDateChange = (checkInDate: string, checkOutDate: string) => {
    setNewCheckIn(checkInDate);
    setNewCheckOut(checkOutDate);
  };
  const handleDatePickerOpen = (datePickerType: DatePickerType) => {
    const openedControl = {
      [DatePickerType.CheckIn]: OpenedFormControl.CHECK_IN,
      [DatePickerType.CheckOut]: OpenedFormControl.CHECK_OUT
    }[datePickerType];
    setOpenedFormControl(openedControl);
  };
  const handleCloseDatePicker = () => {
    setOpenedFormControl(OpenedFormControl.NONE);
    dispatch(setOpenDatePickerType(null));
  };
  const handleDatePickerSubmit = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.DatePickerSubmitButton,
      action: Action.Clicked
    }));
    handleCloseDatePicker();
    updateSearch({
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      rooms: newRooms,
      searchTrigger: SearchTrigger.DatePicker
    });
  };
  const handleGuestsClick = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.GuestPickerInput,
      action: Action.Clicked
    }));
    setOpenedFormControl(OpenedFormControl.GUEST);
  };
  const handleGuestPickerClose = () => {
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleGuestChange = (newRoomsSplit: RoomSplitType[], shouldPerformSearch?: boolean) => {
    const newRooms = roomsSplitToString(newRoomsSplit);
    setNewRooms(newRooms);
    if (shouldPerformSearch) updateSearch({
      rooms: newRooms,
      checkIn: newCheckIn,
      checkOut: newCheckOut,
      searchTrigger: SearchTrigger.GuestPicker
    });
  };
  const handleGuestSubmit = (newRoomsSplit: RoomSplitType[]) => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.GuestPickerSubmitButton,
      action: Action.Clicked
    }));
    handleGuestPickerClose();
    handleGuestChange(newRoomsSplit, true);
  };
  const handleOutsideClick = () => {
    if (checkIn !== newCheckIn || checkOut !== newCheckOut || rooms !== newRooms) {
      handleReset();
    }
  };
  const isCheckInPickerOpen = openedFormControl === OpenedFormControl.CHECK_IN;
  const isCheckOutPickerOpen = openedFormControl === OpenedFormControl.CHECK_OUT;
  const isGuestPickerOpen = openedFormControl === OpenedFormControl.GUEST;
  const isDestinationPickerOpen = openedFormControl === OpenedFormControl.DESTINATION;
  const openedDatePickerType: DatePickerType | null = {
    [OpenedFormControl.CHECK_IN]: DatePickerType.CheckIn,
    [OpenedFormControl.CHECK_OUT]: DatePickerType.CheckOut,
    [OpenedFormControl.GUEST]: null,
    [OpenedFormControl.NONE]: null
  }[openedFormControl];
  const {
    destinationHasError,
    onDestinationPickerInputClick,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onRecentSearchSelected,
    onNearbyPropertiesSuggestionSelected,
    onDestinationChange
  } = useSearchBoxDestinationPicker();
  const handleDestinationClick = () => {
    setOpenedFormControl(OpenedFormControl.DESTINATION);
    onDestinationPickerInputClick();
  };
  const handleCloseSearchBox = () => {
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleRecentSearchSelected = useCallback((recentSearch: RecentSearchParams): void => {
    onRecentSearchSelected({
      recentSearch
    });
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn));
  }, [dispatch, onRecentSearchSelected]);
  const handleDestinationChange = (params: OnDestinationChangeParams) => {
    onDestinationChange(params);
    const {
      type,
      value
    } = params;
    const parameters = {
      // The date should reflect the date picker value, not the recent search date.
      checkIn,
      checkOut,
      rooms,
      ...getDestinationParameter(type as string, value)
    };
    doSearch({
      newUrlParams: {
        ...parameters
      },
      searchTrigger: SearchTrigger.RecentSearches
    });
  };
  const handleSuggestionSelected = useCallback((): void => {
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn));
  }, [dispatch]);
  const handleCloseDestinationPicker = (e: MouseEvent) => {
    // do not close the destination picker if we are clicking within the input container
    if (!destinationInputRef?.current?.contains(e.target as Node)) {
      handleCloseSearchBox();
    }
  };
  const handleNearbyPropertiesSuggestionSelected = useCallback((): void => {
    onNearbyPropertiesSuggestionSelected();
    dispatch(setOpenDatePickerType(DatePickerType.CheckIn));
  }, [dispatch, onNearbyPropertiesSuggestionSelected]);
  const trackEventCallback = useCallback((payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)), [dispatchTeamEvent]);
  const getSearchFormComponent = () => {
    if (brandHasAlternativeSearchForm) {
      return SearchFormHorizontalFullWidth;
    }
    return SearchFormHorizontal;
  };
  const SearchFormComponent = getSearchFormComponent();
  return <>
      <FormContainer ref={containerRef}>
        <OutsideClickHandler display="contents" onOutsideClick={handleOutsideClick}>
          <SearchFormComponent checkIn={newCheckIn} checkOut={newCheckOut} numberOfAdults={numberOfAdults} numberOfChildren={numberOfChildren} numberOfRooms={numberOfRooms} onCheckInDatePickerClick={handleCheckInClick} onCheckOutDatePickerClick={handleCheckOutClick} onGuestPickerClick={handleGuestsClick} onSubmit={handleSearchButtonClick} isCheckInPickerOpen={isCheckInPickerOpen} isCheckOutPickerOpen={isCheckOutPickerOpen} isGuestPickerOpen={isGuestPickerOpen} guestPickerRef={guestPickerRef} {...searchBoxWithDestinationPicker && {
          destinationHasError,
          isDestinationPickerOpen,
          onDestinationPickerClick: handleDestinationClick,
          destinationButtonDisplayValue: destinationDisplayValue,
          destinationInputDisplayValue: destinationPickerInputDisplayValue,
          onSuggestionsRequested: onSuggestionsRequested,
          destinationInputRef
        }} trackEvent={trackEventCallback} />
          {searchBoxWithDestinationPicker && <DestinationPickerPopover isOpen={isDestinationPickerOpen} anchorRef={containerRef} onChange={handleDestinationChange} onSuggestionSelected={handleSuggestionSelected} onRecentSearchSelected={handleRecentSearchSelected} onClose={handleCloseDestinationPicker} onNearbyPropertiesSuggestionSelected={handleNearbyPropertiesSuggestionSelected} />}

          <DatePickerPopover isOpen={isCheckInPickerOpen || isCheckOutPickerOpen} openedDatePickerType={openedDatePickerType} anchorRef={containerRef} checkIn={newCheckIn} checkOut={newCheckOut} onClose={handleCloseDatePicker} onChange={handleDateChange} onDatePickerOpen={handleDatePickerOpen} onSubmit={handleDatePickerSubmit} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} weekdaysShort={i18n.weekDayNamesShort} months={i18n.monthNames} firstDayOfWeek={i18n.firstDayOfWeek} />
          <GuestPickerPopover isOpen={isGuestPickerOpen} anchorRef={guestPickerRef} placement={PopoverPlacement.Left} roomsSplit={roomsSplit} onChange={handleGuestChange} onSubmit={handleGuestSubmit} onClose={handleGuestPickerClose} verticalOffset={POPOVER_VERTICAL_OFFSET} horizontalOffset={POPOVER_HORIZONTAL_OFFSET} />
        </OutsideClickHandler>
      </FormContainer>
    </>;
};

require("./SearchForm.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchForm.tsx");